import { UserAction } from "@habity/types";

// Lead source configuration type
type LeadSourceConfig = {
  readonly LEAD_SOURCE_ID: string | undefined;
  readonly ASSIGNMENT_SOURCE_ID: string | undefined;
  readonly PRIORITIZED: boolean;
};

// Lead source IDs for different user actions in Vitec CRM
// Keys must match UserAction enum values (uppercased) or be 'DEFAULT'
export const VITEC_LEAD_SOURCES: Record<Uppercase<UserAction> | 'DEFAULT', LeadSourceConfig> = {
  DEFAULT: {
    LEAD_SOURCE_ID: 'LETBABF4881F9024F3C8DB820C6EE59FF37', // REQUESTED_BROKER_VALUATION
    ASSIGNMENT_SOURCE_ID: '4058835',
    PRIORITIZED: true
  },
  CLAIMED_HOME: {
    LEAD_SOURCE_ID: 'LET2EA78C2A521147B7B6300CADE1840018',
    ASSIGNMENT_SOURCE_ID: undefined, // '3271390'
    PRIORITIZED: false
  },
  REQUESTED_BROKER_VALUATION: {
    LEAD_SOURCE_ID: 'LETBABF4881F9024F3C8DB820C6EE59FF37',
    ASSIGNMENT_SOURCE_ID: '4058835',
    PRIORITIZED: true
  },
  REQUESTED_BROKER_VALUATION_AFTER_HOME_INTERACTION: {
    LEAD_SOURCE_ID: 'LET31AC93AC0FCC489A89234FF94599109D',
    ASSIGNMENT_SOURCE_ID: '920857',
    PRIORITIZED: true
  },
  REQUESTED_BROKER_VALUATION_AFTER_AVM_ESTIMATE: {
    LEAD_SOURCE_ID: 'LET1D7C7E14723B4C91AA2B1A48B7E59DC2',
    ASSIGNMENT_SOURCE_ID: '2859072',
    PRIORITIZED: true
  },
  PERFORMED_AVM_ESTIMATE: {
    LEAD_SOURCE_ID: 'LET221465F7A52B4E9F95B3565D508B2D87',
    ASSIGNMENT_SOURCE_ID: '2823118',
    PRIORITIZED: true
  },
  MATCHED_HOME_WITH_SPECULATORS: {
    LEAD_SOURCE_ID: undefined,
    ASSIGNMENT_SOURCE_ID: undefined,
    PRIORITIZED: false
  },
  VIEWED_SPECULATORS_OF_HOME: {
    LEAD_SOURCE_ID: undefined,
    ASSIGNMENT_SOURCE_ID: undefined,
    PRIORITIZED: false
  },
  MATCHED_HOME: {
    LEAD_SOURCE_ID: undefined,
    ASSIGNMENT_SOURCE_ID: undefined,
    PRIORITIZED: false
  },
  BOOKED_MEETING: {
    LEAD_SOURCE_ID: 'LETBD2A7848BAD84B57B97EFA2C6870B710',
    ASSIGNMENT_SOURCE_ID: '920857',
    PRIORITIZED: true
  },
  BOOKED_ONLINE_MEETING: {
    LEAD_SOURCE_ID: 'LETBD2A7848BAD84B57B97EFA2C6870B710',
    ASSIGNMENT_SOURCE_ID: '920857',
    PRIORITIZED: true
  },
  SHOWED_INTEREST: {
    LEAD_SOURCE_ID: 'LET1E0F909CEA204681A200D95A482B4B3C',
    ASSIGNMENT_SOURCE_ID: undefined,
    PRIORITIZED: false
  },
  SHOWED_STRONG_INTEREST: {
    LEAD_SOURCE_ID: 'LET1E0F909CEA204681A200D95A482B4B3C',
    ASSIGNMENT_SOURCE_ID: undefined,
    PRIORITIZED: false
  },
  BOOKED_VIEWING: {
    LEAD_SOURCE_ID: 'LET12B2B273C3B642B88089E9B99D4746D1',
    ASSIGNMENT_SOURCE_ID: undefined,
    PRIORITIZED: false
  },
  FOLLOWED_BIDDING: {
    LEAD_SOURCE_ID: 'LET1E0F909CEA204681A200D95A482B4B3C',
    ASSIGNMENT_SOURCE_ID: undefined,
    PRIORITIZED: false
  },
  MATCHED: {
    LEAD_SOURCE_ID: undefined,
    ASSIGNMENT_SOURCE_ID: undefined,
    PRIORITIZED: false
  },
  LIKED: {
    LEAD_SOURCE_ID: undefined,
    ASSIGNMENT_SOURCE_ID: undefined,
    PRIORITIZED: false
  },
  ATTENDED_VIEWING: {
    LEAD_SOURCE_ID: undefined,
    ASSIGNMENT_SOURCE_ID: undefined,
    PRIORITIZED: false
  },
  PAUSED_BIDDING: {
    LEAD_SOURCE_ID: undefined,
    ASSIGNMENT_SOURCE_ID: undefined,
    PRIORITIZED: false
  },
  PLACED_BID: {
    LEAD_SOURCE_ID: undefined,
    ASSIGNMENT_SOURCE_ID: undefined,
    PRIORITIZED: false
  },
  BOOKED_CONTRACT_SIGNING: {
    LEAD_SOURCE_ID: undefined,
    ASSIGNMENT_SOURCE_ID: undefined,
    PRIORITIZED: false
  },
  RESERVED_HOME: {
    LEAD_SOURCE_ID: undefined,
    ASSIGNMENT_SOURCE_ID: undefined,
    PRIORITIZED: false
  },
  DECLINED_INTEREST: {
    LEAD_SOURCE_ID: undefined,
    ASSIGNMENT_SOURCE_ID: undefined,
    PRIORITIZED: false
  },
  ARCHIVED: {
    LEAD_SOURCE_ID: undefined,
    ASSIGNMENT_SOURCE_ID: undefined,
    PRIORITIZED: false
  },
  ADDED_TO_WISHLIST: {
    LEAD_SOURCE_ID: undefined,
    ASSIGNMENT_SOURCE_ID: undefined,
    PRIORITIZED: false
  },
  CREATED_SEARCH_PROFILE: {
    LEAD_SOURCE_ID: undefined,
    ASSIGNMENT_SOURCE_ID: undefined,
    PRIORITIZED: false
  },
  ENTERED_SELLER_LEAD_FORM: {
    LEAD_SOURCE_ID: 'LETBABF4881F9024F3C8DB820C6EE59FF37', // Requested broker valuation, should always come from the form
    ASSIGNMENT_SOURCE_ID: '4058835',
    PRIORITIZED: true
  },
  ENTERED_BUYER_LEAD_FORM: {
    LEAD_SOURCE_ID: undefined,
    ASSIGNMENT_SOURCE_ID: undefined,
    PRIORITIZED: false
  },
  CLICKED_IMAGE_AD: {
    LEAD_SOURCE_ID: undefined, // No lead creation for ad clicks
    ASSIGNMENT_SOURCE_ID: undefined,
    PRIORITIZED: false
  },
  SUBSCRIBED_FINAL_PRICE: {
    LEAD_SOURCE_ID: undefined,
    ASSIGNMENT_SOURCE_ID: undefined,
    PRIORITIZED: false
  },
  CLICKED_PUBLIC_PREVIEW_HOME: {
    LEAD_SOURCE_ID: undefined,
    ASSIGNMENT_SOURCE_ID: undefined,
    PRIORITIZED: false
  },
  // Deprecated: Project viewings now use BOOKED_VIEWING
  BOOKED_PROJECT_VIEWING: {
    LEAD_SOURCE_ID: undefined,
    ASSIGNMENT_SOURCE_ID: undefined,
    PRIORITIZED: false
  },
  BOOKED_BROKER_VALUATION: {
    LEAD_SOURCE_ID: 'LETBABF4881F9024F3C8DB820C6EE59FF37', // Same as REQUESTED_BROKER_VALUATION
    ASSIGNMENT_SOURCE_ID: '4058835',
    PRIORITIZED: true
  },
  REQUESTED_MEETING: {
    LEAD_SOURCE_ID: 'LETBD2A7848BAD84B57B97EFA2C6870B710', // Same as BOOKED_MEETING
    ASSIGNMENT_SOURCE_ID: '920857',
    PRIORITIZED: true
  }
} as const;

export type VitecLeadSourceKey = keyof typeof VITEC_LEAD_SOURCES;

/**
 * Helper function to get lead source and assignment source IDs based on intent
 * @param userAction - The action performed by the user
 * @param customLeadSourceId - Optional custom lead source ID to override
 * @param customAssignmentSourceId - Optional custom assignment source ID to override
 * @returns Object with leadSourceId and assignmentSourceId
 */
export const getVitecLeadSources = (
  userAction?: UserAction,
  customLeadSourceId?: string,
  customAssignmentSourceId?: string
): { leadSourceId: string | undefined; assignmentSourceId: string | undefined; skipLeadCreation: boolean } => {
  // Get source config based on user action
  const userActionKey = userAction?.toUpperCase() as keyof typeof VITEC_LEAD_SOURCES;
  const sourceConfig = VITEC_LEAD_SOURCES[userActionKey] || VITEC_LEAD_SOURCES.DEFAULT;

  const leadSourceId = customLeadSourceId || sourceConfig.LEAD_SOURCE_ID;

  return {
    leadSourceId,
    assignmentSourceId: customAssignmentSourceId !== undefined ? customAssignmentSourceId : sourceConfig.ASSIGNMENT_SOURCE_ID,
    skipLeadCreation: !leadSourceId,
  };
};
